/* ATUAL × SUGERIDO
 * A coluna Sugerido só existe onde há reajuste a fazer (PREJUÍZO e ABAIXO).
 * Nos estados NO PONTO e ACIMA o mínimo para a meta é referência do veredito,
 * não alvo: imprimi-lo como "Sugerido" abaixo do preço atual seria sugerir
 * reduzir preço. */

import { pedeReajuste } from '@favo/core';

import { TabelaCenarios, type LinhaCenario } from '@/componentes/tabela-cenarios';
import { Valor } from '@/componentes/valor';
import type { Cenario, Parametros, Recomendacao } from '@/dados';

import { sinalMoeda, sinalPontos, sinalQuantidade } from './grandezas';

export function Comparativo({
  rec,
  p,
  atual,
}: {
  rec: Recomendacao;
  p: Parametros;
  atual: Cenario;
}) {
  const sug = pedeReajuste(rec) ? rec.sugerido : null;

  /** Compara duas grandezas do motor e diz se a mudança é boa. Só exibição. */
  function delta(
    a: number | null,
    b: number | null | undefined,
    maiorEMelhor: boolean,
    escrever: (v: number) => string,
  ): Pick<LinhaCenario, 'delta' | 'tomDelta'> {
    if (a === null || b === null || b === undefined) return {};
    const d = b - a;
    if (d === 0) return { delta: 'sem mudança', tomDelta: 'neutro' };
    return {
      delta: escrever(d),
      tomDelta: d > 0 === maiorEMelhor ? 'positivo' : 'negativo',
    };
  }

  const linhas: LinhaCenario[] = [
    {
      metrica: 'Preço de venda',
      nota: 'por projeto',
      atual: <Valor valor={p.ticket} formato="moeda" />,
      ...(sug ? { sugerido: <Valor valor={sug.ticket} formato="moeda" /> } : {}),
      ...delta(p.ticket, sug?.ticket, true, sinalMoeda),
    },
    {
      metrica: 'Margem de contribuição',
      nota: 'sobre o preço',
      atual: <Valor valor={atual.mcPct} formato="percentual" />,
      ...(sug ? { sugerido: <Valor valor={sug.mcPct} formato="percentual" /> } : {}),
      ...delta(atual.mcPct, sug?.mcPct, true, sinalPontos),
    },
    {
      metrica: 'Lucro líquido',
      nota: 'sobre o preço, já com a estrutura',
      atual: <Valor valor={atual.lucroPct} formato="percentual" />,
      ...(sug ? { sugerido: <Valor valor={sug.lucroPct} formato="percentual" /> } : {}),
      ...delta(atual.lucroPct, sug?.lucroPct, true, sinalPontos),
    },
    {
      metrica: 'Lucro no mês',
      nota: 'média do volume anual',
      atual: <Valor valor={atual.mensal.lucro} formato="moeda" />,
      ...(sug ? { sugerido: <Valor valor={sug.mensal.lucro} formato="moeda" /> } : {}),
      ...delta(atual.mensal.lucro, sug?.mensal.lucro, true, sinalMoeda),
    },
    {
      metrica: 'Equilíbrio em projetos',
      nota: 'projetos por mês para pagar a estrutura',
      atual: <Valor valor={atual.peQtd} formato="numero" decimais={1} />,
      ...(sug
        ? {
            sugerido: <Valor valor={sug.peQtd} formato="numero" decimais={1} />,
          }
        : {}),
      // A diferença sai dos valores CRUS do motor e é arredondada UMA vez, na
      // mesma casa das duas colunas. Subtrair os números já arredondados (3,3
      // menos 1,4) daria outro resultado, e numa ferramenta que existe para
      // acertar número não há delta aproximado que se defenda.
      ...delta(atual.peQtd, sug?.peQtd, false, sinalQuantidade),
    },
    {
      metrica: 'Equilíbrio em faturamento',
      nota: 'faturamento do mês para pagar a estrutura',
      atual: <Valor valor={atual.peFat} formato="moeda" />,
      ...(sug ? { sugerido: <Valor valor={sug.peFat} formato="moeda" /> } : {}),
      ...delta(atual.peFat, sug?.peFat, false, sinalMoeda),
    },
  ];

  return (
    <TabelaCenarios
      linhas={linhas}
      legenda={
        sug
          ? 'Indicadores do projeto no preço praticado e no preço sugerido, lado a lado.'
          : 'Indicadores do projeto no preço praticado.'
      }
      {...(sug ? { colunaSugerido: 'Sugerido' } : {})}
      rodape={
        sug
          ? 'O cenário sugerido é o mesmo projeto vendido no piso da meta. Ponto de equilíbrio menor é melhor: cada projeto passa a cobrir mais estrutura.'
          : undefined
      }
    />
  );
}
